/**
 * The TEST-ONLY `window.__combatBoardDebug` hook — its global `Window` augmentation and the factory
 * that builds the object `<CombatStage>`'s mount effect installs. Both moved verbatim out of
 * `../CombatStage`; the accessors' bodies, the ref reads, and the doc comments are unchanged.
 */
import type { Container } from "pixi.js";
import type { CellSightline } from "../../../lib/encounter/combatInspector";
import { getCombatBoardBuildCount, type CombatBoardHandle } from "../pixiCombatBoard";
import type { PlacedWhere } from "../labelLayout";
import type { TokenChrome } from "../../../lib/encounter/tokenChrome";
import type { TerrainBuildStats } from "../terrainArt/terrainLayer";
import type { MeasureDebug } from "../combatApp/useMeasureTool";
import { subscribeDiceThrows } from "../../../lib/dice/diceController";

declare global {
  // Module augmentation of the global `Window` requires `interface` (declaration merging) — the same
  // sanctioned exception `router.tsx`'s `@tanstack/react-router` `Register` augmentation takes.
  // oxlint-disable-next-line typescript/consistent-type-definitions
  interface Window {
    /** TEST-ONLY / dev-console hook — a non-visual read/write surface onto the live board,
     *  populated only while it's mounted (see the mount effect below).
     *  `getTokenPos` (`frontend/e2e/token-slide-parity.e2e.ts`) reads
     *  a token's LIVE Pixi container position (the same position `pixiAnimator.ts` tweens and
     *  `pixiCombatBoard.ts`'s `setTokens` reconciles) without scraping rendered pixels.
     *  `getTokenScreenPos` (the token-snapback drag guard, `frontend/e2e/token-snapback.e2e.ts`) is
     *  its screen-space companion: it returns the token's live position in CANVAS-relative CSS pixels
     *  (the Pixi global position, i.e. the view transform already applied), so a flow can drive a real
     *  mouse gesture AT the token's rendered location without reconstructing the stage's pan/zoom — the
     *  centering formula the other flows use silently mis-locates a token whenever the canvas isn't the
     *  full wrap height (e.g. EDIT mode's authoring rail shrinks it).
     *  `getMaxFPS`/`setMaxFPS`/`getStats` proxy `pixiCombatBoard.ts`'s native
     *  `Ticker.maxFPS` cap + independently-measured fps, for the permanent FPS-cap regression guard
     *  (`frontend/e2e/combat-fps-cap.e2e.ts`) and for live console tuning (e.g.
     *  `__combatBoardDebug.setMaxFPS(30)`) — the replacement for the deleted `frameGovernor.ts`'s
     *  `window.lvttSetMaxFps` dev knob. `getGhostPos` (,
     *  `frontend/e2e/ghost-drag.e2e.ts`) reads the PLAY-mode drag-ghost's live container position by
     *  combatant id (`undefined` when none exists), the same non-visual-read pattern. Extend this hook
     *  (don't invent a parallel one) if a future e2e flow needs another cheap, non-visual read off the
     *  live board. */
    __combatBoardDebug?: {
      getTokenPos: (id: string) => { x: number; y: number } | undefined;
      getTokenScreenPos: (id: string) => { x: number; y: number } | undefined;
      /** The token container's LIVE opacity — the one product of every dimming axis
       *  `renderTokenLook` (`pixiCombatBoard.ts`) multiplies together (GM `concealed`,
       *  un-targetable, Destroyed, open door; RAW `Hidden` no longer dims, see `getTokenHolo`). Pixi container alpha genuinely isn't
       *  DOM-readable, which is why every earlier flow had to assert on *behaviour* instead; this
       *  reads it straight off the same registered node `getTokenPos` samples, so a real-browser
       *  flow can prove a VISUAL treatment (`hidden-enemy.e2e.ts` reads it to prove Hidden does NOT dim) rather than
       *  only its gating side effects. `undefined` for an unplaced/unbuilt token. */
      getTokenAlpha: (id: string) => number | undefined;
      /** Whether the token draws the RAW `Hidden` holo-ghost look (`pixiTokenSprite.ts`'s
       *  `applyHoloGhost`, found by its `"holo-ghost"` node label). `undefined` for an unbuilt token. */
      getTokenHolo: (id: string) => boolean | undefined;
      /** A cell's CENTER in canvas-relative CSS pixels, keyed `"x,y"` — the cell-space twin of
       *  {@link getTokenScreenPos}, and the seam a flow uses to HOVER a known square. The pane under
       *  test (`TileInfoBody`, `terrain-legibility` Phase 2) describes ground, so the cells worth
       *  hovering are deliberately empty and there is no token to aim at; reconstructing the point
       *  from the stage's pan/zoom in the flow is exactly what `getTokenScreenPos`' doc warns
       *  against. `undefined` for a malformed key. */
      getCellScreenPos: (key: string) => { x: number; y: number } | undefined;
      getMaxFPS: () => number;
      setMaxFPS: (fps: number) => void;
      getStats: () => { measuredFps: number; maxFPS: number; frameCount: number; rendererKind: string };
      /** The renderer resolution against the page's `devicePixelRatio`, and the canvas backing store
       *  against its CSS box (`projects/crisp-zoom.md` 2.3): `backing` is `css × min(dpr, 2)`. */
      getRenderResolution: () => { resolution: number; dpr: number; backing: [number, number]; css: [number, number] };
      /** The board text raster (`projects/crisp-zoom.md` 3.2): the settled scale and its level (1, 2
       *  or 4), how long the render that last re-rasterized took, and every board text counted by
       *  its resolution. */
      getTextRaster: () => { scale: number; level: number; lastMs: number; resolutions: Record<string, number> };
      /** the terrain layer's build timings, chunk count and texture bytes (`terrain-tile-art` 4.6).
       *  A build is timed from `setTerrain` to the end of the render that uploads its textures;
       *  poll until `busy` is false and `builds` has advanced before reading a number. */
      getTerrainBuild: () => TerrainBuildStats;
      /** whether the PLAY full-grid layer is showing. */
      getFullGrid: () => boolean;
      /** the terrain mechanics overlay (`terrain-mechanics-overlay` 3.4): whether it is on, and each
       *  drawn piece with its full and compact labels, the text it shows now, its cells and label cell. */
      getTerrainMechanics: () => ReturnType<CombatBoardHandle["debugTerrainMechanics"]>;
      getAttackOverlayCount: () => number;
      getGhostPos: (id: string) => { x: number; y: number } | undefined;
      getMovePathCount: () => number;
      /** How many cells the drag trail shows in the boost colour (the part only a folded Boost pays for). */
      getMovePathBoostCells: () => number;
      /** Whether the drawn move path is a refused (red) Trampler trail. */
      getMovePathRefused: () => boolean;
      /** `jericho-directed-placement` D1 follow-up — how many pending directed-deploy marks are mounted right
       *  now: `0` before the first pick and after the gesture ends (commit/cancel/Esc/back), `N` after
       *  N picks. The seam a browser flow reads to prove a section appears AT pick 1 rather than only
       *  when both cells commit — the marks are canvas nodes, so there is no DOM to query. */
      getPendingDeployCount: () => number;
      /**
       * every damage/MISS float this board has spawned, with
       * the `performance.now()` at which it reached the screen. The reveal gate's claim is an
       * ORDERING ("the outcome was not disclosed before its dice settled"), and a float is a Pixi
       * node with no DOM, so a browser flow has no other way to timestamp the disclosure. Read
       * against `window.__diceStageDebug.lastSettledAt` for the other side of the comparison.
       */
      getFloatLog: () => { text: string; at: number }[];
      /** The attack looks played so far, oldest first: each travel and outcome with its family, the
       *  outcome, and its `performance.now()` stamp (`attack-animations` 4.1). Compare a stamp with
       *  `getFloatLog`'s to order an outcome against the float that discloses it. */
      getAttackFxLog: () => ReturnType<CombatBoardHandle["debugAttackFxLog"]>;
      /**
       * Stand in for the 3D dice, which this headless browser cannot draw: every combat resolution
       * throw from now on is claimed and lands `ms` later, as a real tumble would, and its landing
       * time joins `getDiceLandLog`. `0` stops standing in (throws release at once again). This is
       * what lets a flow prove an outcome waits for the dice rather than asserting an order that
       * cannot fail (`dice-land-before-reveal.e2e.ts` explains why the bare harness can't).
       */
      simulateDice: (ms: number) => void;
      /** When each simulated throw landed (`performance.now()`), oldest first. */
      getDiceLandLog: () => number[];
      /** Every change of the chain link's focus ring (`chained-resolution-sequence` 2.2): the ringed
       *  token ids (`[]` when the chain ends) and when. The ring is a Pixi node with no DOM. */
      getChainFocusLog: () => { ids: string[]; at: number }[];
      /** the move/tech washes' non-visual read, alongside
       *  `getAttackOverlayCount`, so an e2e flow can prove overlay-mode exclusivity (exactly one of
       *  the three counts non-zero at a time) without scraping rendered pixels. NOT a per-cell count:
       *  a band is a few Pixi nodes (wash pieces, edge, pips), so read it as 0 / non-zero.
       *  Use `getMoveRangeKeys` for an exact count or content assertion. */
      getMoveRangeCount: () => number;
      /** The boost ring's node count (`setBoostRange`), the move wash's violet outer band. */
      getBoostRangeCount: () => number;
      /** The ids the command menu's target rings are on (`setTargetRings`). */
      getTargetRingIds: () => string[];
      /** The rings' last phase, and each ringed id's spin-in start (ms), `null` for a ring that did not spin in. */
      getTargetRingSpin: () => { phase: "hover" | "armed"; bornAt: Record<string, number | null> };
      /** The annotation ids the board draws now; a stroke the open eraser drag has passed over is
       *  already missing here, before the release commits the erase. */
      getAnnotationIds: () => string[];
      /** The armed attack's mini forecast badges (`setTargetBadges`): drawn text, world-space rect,
       *  placement kind and whether each is on screen. `{}` when no attack is armed. */
      getTargetBadges: () => Record<string, { text: string; x: number; y: number; w: number; h: number; where: PlacedWhere; visible: boolean }>;
      /** The token ids drawn with the "mine" ring (`controlsCombatant` for the viewer, G-D7). */
      getMineTokenIds: () => string[];
      /** Each placed token's resolved art: `tokenImageFor` tier, the texture URL requested (`null` for
       *  a generic ring), and whether it has decoded onto the board (`pixiTokenSprite.ts`). */
      getTokenArt: () => Record<string, { tier: string; url: string | null; loaded: boolean }>;
      /** The turn state + vitals a placed token is drawn with (`tokenChromeFor`, projected for this
       *  viewer), or `undefined` for an unplaced or absent unit. The PLAY turn surface's probe:
       *  `.state` is the activation state its mark shows, `.hp === null` means no HP bar is drawn. */
      getTokenChrome: (id: string) => TokenChrome | undefined;
      /** The token the active-unit beacon rings and whether it pulses, or `null` (`turn-presence` 2.2). */
      getActiveBeacon: () => { id: string; pulse: boolean } | null;
      /** Each token's turn-state row above its footprint, world space like `getLabelBounds` — the
       *  label solver's obstacles. Absent for a token with nothing outside its footprint. */
      getChromeRowBounds: () => Record<string, { x: number; y: number; w: number; h: number }>;
      getSensorRangeCount: () => number;
      /** How many hover-intel weapon rings are drawn (`setThreatRings`). */
      getThreatRingCount: () => number;
      /** Each drawn hover-intel ring's cell keys (`"x,y"`), sorted: where a ring is centred. */
      getThreatRingKeys: () => string[][];
      /** How many cover-hatch cells lie under the hover-intel rings (`range-preview-cover`). */
      getThreatRingHatchCount: () => number;
      /** the footprint cell KEYS (`"x,y"`, matching every other
       *  key-set read here) of any combatant the CURRENTLY-hovered armed AoE template catches
       *  geometrically but the measuring center can't SEE (`attackLosExcluded`'s live result — the
       *  cells `attackAffectedCombatantKeys` drops that combatant's id from the true hit set for).
       *  `[]` when nothing's armed/hovered or nothing's excluded — the non-visual read an e2e flow
       *  uses to prove a sheltered token's cell is marked while an exposed token's cell in the SAME
       *  template isn't, without asserting on Pixi container alpha (not DOM-readable). */
      getLosExcludedKeys: () => string[];
      /** the CURRENTLY-hovered armed template's own
       *  cell-key set (`attackHover`'s live result, the same "x,y" vocabulary every other key-set
       *  read here uses): the aimed cell plus its Blast/Line/Cone/Burst area, exactly what
       *  `attackOverlayContainer` paints red. `[]` when nothing's armed/hovered. The non-visual read
       *  an e2e flow uses to prove the painted template equals the resolver's true hit set, without
       *  scraping rendered pixels — the twin of `getLosExcludedKeys` for the template itself rather
       *  than its LoS-excluded markers. */
      getAttackAffectedKeys: () => string[];
      /** the live `AreaEffect` cloud fill count (`setAreaEffects`'
       *  own container), the same cheap non-visual-read shape as `getAttackOverlayCount`. `0` when no
       *  cloud is live, `≥1` once a throw has minted one; used to establish the positive window before
       *  asserting a dispersed cloud's absence (a hollow-negative guard — see the worktree `CLAUDE.md`'s
       *  "Assertion races" section). */
      getAreaEffectCount: () => number;
      /** the drone-aura ring count (`setDroneAuras`): one per placed, not Destroyed drone with an aura. */
      getDroneAuraCount: () => number;
      /** the CURRENT directed-deploy legal-cell wash
       *  (D3/D4/D6): the same "x,y" key-set vocabulary every other read here uses, sourced from the
       *  `attackRange` prop (which `CombatApp.tsx` folds `legalDirectedDeployCells`'s result into
       *  while `activate_sites` is armed — see that file's `directedDeployWash`/`attackRange` memos).
       *  Meaningful only while a directed-placement draft (Jericho's two sections, the turret's one)
       *  is armed; `[]` otherwise, or once nothing is armed. `attackRange` also carries the ORDINARY
       *  per-weapon reach wash for Skirmish/Barrage/`activate_cell` — this accessor doesn't
       *  distinguish which mode is live, so a caller should only read it while
       *  `activate_sites` is the thing armed. Non-visual — the twin of `getAttackAffectedKeys` for this
       *  wash, so a flow can assert the rendered wash without scraping rendered pixels. */
      getDirectedDeployWashKeys: () => string[];
      /** the CURRENT movement-range wash's cell-key set (the same
       *  `"x,y"` vocabulary every other key-set read here uses), mirroring the `reachable` prop. The
       *  key-set twin of `getMoveRangeCount`: a count alone can't distinguish "walled off" reach from
       *  "pathed straight through the wall" reach when the two happen to total the same number of
       *  cells, but the exact SET can. `[]` when nothing is selected / the unit is unplaced. */
      getMoveRangeKeys: () => string[];
      /** the CURRENT per-cell cover advisory (`CellSightline.cover`)
       *  at a given cell key, or `undefined` if that key isn't in the live reach wash at all (nothing
       *  armed, or the cell is out of the armed weapon's range). Mirrors `cellSightlines`, the SAME
       *  data `pixiCombatBoard.ts`'s `setTargeting` paints the cover hatch from — the non-visual read
       *  an e2e flow uses to prove a player's own cover advisory genuinely reads a cover-granting
       *  object (a crate) rather than silently reporting "none" because the object never reached this
       *  wire. */
      getCoverAt: (key: string) => CellSightline["cover"] | undefined;
      /** every LIVE remote cursor's slot id paired with its ACTUAL
       *  rendered chip text, read straight off the mounted Pixi `Text` node (not the data model), so
       *  a browser flow can prove the PIXELS say a callsign rather than only proving the `cursors`
       *  prop carried one. `[]` when no remote cursor is live. */
      getCursorLabels: () => { by: string; label: string }[];
      /** every LIVE token's/label's world-space bbox, keyed by
       *  combatant id, read straight off the mounted Pixi containers/`Text` (not a data model), so
       *  `label-layout.e2e.ts` can measure the ACTUAL rendered geometry: today's static under-token
       *  layout collides at these numbers, and the solver-driven layout resolves it. `{}` when
       *  the board holds no tokens. */
      getTokenBounds: () => Record<string, { x: number; y: number; w: number; h: number }>;
      getLabelBounds: () => Record<string, { x: number; y: number; w: number; h: number; text: string }>;
      /** every LIVE label's placement KIND (`labelLayout.ts`'s
       *  `PlacedWhere`) plus whether its pill is actually drawn. `getLabelBounds` above reports a
       *  rect ONLY for a drawn pill, so a label the solver HID is absent from it — correct, since it
       *  occupies no pixels, but indistinguishable from "this token has no label". This read is what
       *  tells the two apart, and it is how `label-layout.e2e.ts` asserts a boxed-in unit's label is
       *  hidden and that hovering/selecting the token reveals it. `revealed` is whether the label is
       *  in the raised hover/selection layer right now, which is what distinguishes a pill that is
       *  drawn because the solver PLACED it from one drawn because the pointer is on its token. */
      getLabelStates: () => Record<string, { where: PlacedWhere; drawn: boolean; revealed: boolean }>;
      /** convenience mirror of the LOAD-BEARING
       *  `window.__combatBoardBuildCount()` (`pixiCombatBoard.ts`): this object is deleted on every
       *  unmount, so a flow proving "the renderer was not rebuilt across a drop" must read the
       *  persistent twin, not this one — see that export's doc for why. */
      getBuildCount: () => number;
      /** The Measure tool's rulers (`useMeasureTool`): the local one, with its ends, the engine's
       *  distance and sight, and the counted cells as `x,y` keys, or `null` when none is showing.
       *  `remote` is the table's shared rulers (Phase 4). */
      getMeasure: () => MeasureDebug;
    };
  }
}

/**
 * Build the `window.__combatBoardDebug` object for a freshly-mounted board. Every accessor reads
 * through the handle or a REF (never React state directly) — this object is captured once at mount,
 * so only a `.current` read stays live across every subsequent hover recompute.
 */
export function buildBoardDebugHook({
  handle,
  getNode,
  floatLogRef,
  chainFocusLogRef,
  attackLosExcludedRef,
  attackAffectedRef,
  attackRangeRef,
  reachableRef,
  cellSightlinesRef,
  measureDebugRef,
}: {
  handle: CombatBoardHandle;
  getNode: (id: string) => Container | undefined;
  floatLogRef: { current: { text: string; at: number }[] };
  chainFocusLogRef: { current: { ids: string[]; at: number }[] };
  attackLosExcludedRef: { current: Set<string> | null };
  attackAffectedRef: { current: Set<string> | null };
  attackRangeRef: { current: Set<string> | null };
  reachableRef: { current: Set<string> | null };
  cellSightlinesRef: { current: CellSightline[] | null };
  measureDebugRef: { current: MeasureDebug };
}): NonNullable<Window["__combatBoardDebug"]> {
  // `simulateDice`: a stand-in visualizer, subscribed only while a flow asks for one.
  let simulatedMs = 0;
  let unsubscribeDice: (() => void) | null = null;
  const diceLandLog: number[] = [];
  const simulateDice = (ms: number): void => {
    simulatedMs = ms;
    if (ms <= 0) {
      unsubscribeDice?.();
      unsubscribeDice = null;
      return;
    }
    unsubscribeDice ??= subscribeDiceThrows((req, ticket) => {
      if (req.kind !== "steps" || simulatedMs <= 0) return;
      ticket.claim();
      setTimeout(() => {
        diceLandLog.push(performance.now());
        ticket.settle();
      }, simulatedMs);
    });
  };
  return {
    getTokenPos: (id) => { const n = getNode(id); return n ? { x: n.x, y: n.y } : undefined; },
    getTokenScreenPos: (id) => { const n = getNode(id); const g = n?.getGlobalPosition(); return g ? { x: g.x, y: g.y } : undefined; },
    getTokenAlpha: (id) => getNode(id)?.alpha,
    getTokenHolo: (id) => { const n = getNode(id); return n ? n.getChildByLabel("holo-ghost", true) !== null : undefined; },
    getCellScreenPos: (key) => handle.debugCellScreenPos(key),
    getMaxFPS: () => handle.getMaxFPS(),
    setMaxFPS: (fps) => handle.setMaxFPS(fps),
    getStats: () => handle.getStats(),
    getRenderResolution: () => handle.debugRenderResolution(),
    getTextRaster: () => handle.debugTextRaster(),
    getTerrainBuild: () => handle.debugTerrainBuild(),
    getFullGrid: () => handle.debugFullGrid(),
    getTerrainMechanics: () => handle.debugTerrainMechanics(),
    getAttackOverlayCount: () => handle.debugAttackOverlayCount(),
    getAreaEffectCount: () => handle.debugAreaEffectCount(),
    getDroneAuraCount: () => handle.debugDroneAuraCount(),
    getGhostPos: (id) => handle.debugGhostPos(id),
    getAttackFxLog: () => handle.debugAttackFxLog(),
    simulateDice,
    getDiceLandLog: () => diceLandLog.slice(),
    getMovePathCount: () => handle.debugMovePathCount(),
    getMovePathBoostCells: () => handle.debugMovePathBoostCells(),
    getMovePathRefused: () => handle.debugMovePathRefused(),
    getPendingDeployCount: () => handle.debugPendingDeployCount(),
    getFloatLog: () => floatLogRef.current.slice(),
    getChainFocusLog: () => chainFocusLogRef.current.slice(),
    getMoveRangeCount: () => handle.debugMoveRangeCount(),
    getBoostRangeCount: () => handle.debugBoostRangeCount(),
    getTargetRingIds: () => handle.debugTargetRingIds(),
    getTargetRingSpin: () => handle.debugTargetRingSpin(),
    getAnnotationIds: () => handle.debugAnnotationIds(),
    getTargetBadges: () => handle.debugTargetBadges(),
    getMineTokenIds: () => handle.debugMineTokenIds(),
    getTokenArt: () => handle.debugTokenArt(),
    getTokenChrome: (id) => handle.debugTokenChrome()[id],
    getActiveBeacon: () => handle.debugActiveBeacon(),
    getChromeRowBounds: () => handle.debugChromeRowBounds(),
    getSensorRangeCount: () => handle.debugSensorRangeCount(),
    getThreatRingCount: () => handle.debugThreatRingCount(),
    getThreatRingKeys: () => handle.debugThreatRingKeys(),
    getThreatRingHatchCount: () => handle.debugThreatRingHatchCount(),
    // Reads the REF (never the React state directly) — this closure is captured once at mount, so
    // only a `.current` read stays live across every subsequent hover recompute.
    getLosExcludedKeys: () => Array.from(attackLosExcludedRef.current ?? []),
    getAttackAffectedKeys: () => Array.from(attackAffectedRef.current ?? []),
    getDirectedDeployWashKeys: () => Array.from(attackRangeRef.current ?? []),
    getMoveRangeKeys: () => Array.from(reachableRef.current ?? []),
    getCoverAt: (key) => cellSightlinesRef.current?.find((sl) => sl.key === key)?.cover,
    getCursorLabels: () => handle.debugCursorLabels(),
    getTokenBounds: () => handle.debugTokenBounds(),
    getLabelBounds: () => handle.debugLabelBounds(),
    getLabelStates: () => handle.debugLabelStates(),
    getBuildCount: () => getCombatBoardBuildCount(),
    getMeasure: () => measureDebugRef.current,
  };
}
